<script setup lang="ts">
  import { LazyModalHeroVideo } from '#components';

  const { activeBreakpoint } = useDeviceType();
  const { orientation } = useOrientation();

  const isMobile = computed(() => ['xxs', 'xs', 'sm'].includes(activeBreakpoint.value));

  const DESKTOP_PREVIEW_SRC = '/images/andy-ruiz/banner-image-desktop.webp';
  const VIDEO_BUTTON_SRC = '/images/andy-ruiz/video-play-button.webp';
  const andyAlt = 'Play with Andy Ruiz Jr. and be the next champion';

  const gtag = useGtag();
  const overlay = useOverlay();
  let videoModal: Awaited<ReturnType<typeof overlay.create>> | null = null;

  async function showVideo() {
    if (!videoModal) {
      videoModal = overlay.create(LazyModalHeroVideo);
    }

    await videoModal.open();
  }

  function openSignUpModal() {
    gtag.reportGtagConversion();
    stores.modals.showSignUpModal();
  }

  useSchemaOrg([
    defineVideo({
      name: andyAlt,
      thumbnailUrl: DESKTOP_PREVIEW_SRC,
      embedUrl: `https://maxduel.com/videos/andy-ruiz/MaxDuel_Andy-Ruiz-Jr_9-16_Website.mp4`,
    }),
  ]);
</script>

<template>
  <div
    :key="`andy-ruiz-banner-${activeBreakpoint}-${orientation}`"
    class="relative w-full h-full rounded-[10px] overflow-hidden bg-cover bg-no-repeat bg-[position:0_0] flex flex-col justify-between border border-[#0080ff] min-[720px]:block min-[720px]:relative min-[720px]:shadow-[inset_0_0_0_1px_#0080ff]"
  >
    <!-- Mobile Layout -->
    <template v-if="isMobile">
      <div
        class="absolute inset-0 w-full h-full z-0 rounded-[10px] bg-[#0F0F0F] bg-gradient-to-r from-[#0F0F0F] to-[#141d2700]"
      >
        <div
          class="absolute bottom-0 -right-[45%] -rotate-[20deg] bg-[#0080ff] w-[6.25rem] h-[70%] rounded-full blur-[10px] shadow-[0_0_0_100px_#0080ff] opacity-15"
        />
      </div>

      <div @click="showVideo">
        <p
          class="relative font-barlow text-white text-center m-0 px-0 mt-[27px] text-[clamp(14px,3.75vw,20px)] leading-[120%] mb-2 font-extrabold text-shadow-banner"
        >
          FACE FORMER HEAVYWEIGHT CHAMPION
        </p>
        <p
          class="relative font-barlow text-white text-center m-0 px-0 text-[clamp(19px,4.5vw,22px)] leading-[120%] font-extrabold text-shadow-banner"
        >
          <b class="text-[#5bb5fa] font-black italic">ANDY RUIZ JR.</b>
          AND WIN BIG
        </p>

        <div
          class="relative w-full aspect-[560/598] flex items-center justify-center mb-[calc(-7*max(16px,4vw))] max-w-[400px] mx-auto min-[450px]:mb-[calc(-6*max(16px,4vw))] min-[466px]:mb-[calc(-7*max(16px,4vw))] min-[610px]:mb-[calc(-6*max(16px,4vw))]"
          @click="showVideo"
        >
          <img
            class="absolute inset-0 w-[calc(100%-2px)] max-h-full object-contain mx-auto"
            src="/images/andy-ruiz/banner-image-mobile.webp"
            srcset="/images/andy-ruiz/banner-image-mobile.webp 1x, /images/andy-ruiz/banner-image-mobile.webp 2x"
            sizes="(max-width: 400px) 100vw, 400px"
            :alt="andyAlt"
            fetchpriority="high"
          />
          <NuxtImg
            format="webp"
            densities="x1 x2"
            role="button"
            class="relative w-[110px] h-[110px] cursor-pointer translate-y-5"
            :src="VIDEO_BUTTON_SRC"
            width="110"
            height="110"
            alt="Click to play video"
            aria-label="Click to play video"
            preload
          />
        </div>

        <p
          class="relative font-barlow text-white text-center m-0 px-0 text-[clamp(31px,8.5vw,36px)] leading-[80%] font-black italic mt-3 mb-2 text-shadow-banner"
        >
          SIGN UP
          <b class="text-[#5bb5fa] font-black italic">NOW</b>
        </p>
        <p
          class="relative font-barlow text-white text-center m-0 px-0 text-[clamp(21px,5.5vw,26px)] leading-none italic font-black mb-2 text-shadow-banner"
        >
          AND TRY THE GAMES
        </p>
        <p
          class="relative font-barlow text-white text-center m-0 px-0 text-[clamp(21px,5.5vw,26px)] leading-none italic font-black text-shadow-banner"
        >
          FOR
          <b class="text-[#5bb5fa] font-black italic">FREE</b>
        </p>
      </div>

      <div class="relative text-center p-[max(16px,4vw)] pt-0">
        <button
          class="mt-6 w-full max-w-[400px] rounded-[6px] border border-[#0a9a49] bg-[#0a9a49] text-white text-center font-inter text-base font-bold leading-none tracking-[0.16px] uppercase py-4 px-8 h-[52px] cursor-pointer mx-auto hover:brightness-[103%] active:brightness-[97%]"
          @click="openSignUpModal"
        >
          Play Now
        </button>
      </div>
    </template>

    <!-- Desktop Layout -->
    <template v-else>
      <NuxtImg
        src="/images/andy-ruiz/banner-bg-desktop.webp"
        preload
        format="webp"
        densities="x1 x2"
        class="absolute inset-0 w-full h-full z-0 rounded-[10px]"
      />

      <div
        class="absolute w-[1320px] h-[1684px] rotate-[15deg] shrink-0 rounded-full bg-[#0080ff] blur-[180px] left-[30px]"
      />

      <div
        class="relative z-[1] flex flex-col justify-between max-w-[60%] h-full p-[40px_0_40px_40px] min-[900px]:p-[36px_0_36px_24px] lg:p-[40px_0_40px_24px] xl:p-[40px_0_40px_36px] desktop-1660:p-[48px_0_40px_40px]"
      >
        <div class="xl:h-full">
          <p
            class="relative font-barlow text-white text-left m-0 px-0 mb-2 text-[clamp(15px,2.2vw,21px)] font-extrabold leading-[120%] min-[900px]:text-[13px] lg:text-[13px] lg:mb-0 xl:text-[14px] xl:m-[0_0_5px] desktop-1660:text-[21px] desktop-1660:mb-1 text-shadow-banner"
          >
            FACE FORMER HEAVYWEIGHT CHAMPION
          </p>
          <p
            class="relative font-barlow text-white text-left m-0 px-0 text-[clamp(18px,2.5vw,24px)] font-extrabold leading-[120%] min-[900px]:text-[16px] lg:text-[17px] lg:mb-0 xl:text-[18px] xl:m-0 desktop-1660:text-[21px] text-shadow-banner"
          >
            <b class="text-[#5bb5fa] font-black italic desktop-1660:text-[24px]">ANDY RUIZ JR.</b>
            AND WIN BIG
          </p>

          <p
            class="relative font-barlow text-white text-left m-0 px-0 mt-3 text-[clamp(34px,4.7vw,41px)] font-black italic leading-[80%] min-[900px]:text-[20px] lg:text-[22px] lg:mb-0 xl:text-[22px] xl:m-[32px_0_8px] desktop-1660:text-[46px] desktop-1660:mb-1 text-shadow-banner"
          >
            SIGN UP
            <b class="text-[#5bb5fa] font-black italic">NOW</b>
          </p>
          <p
            class="relative font-barlow text-white text-left m-0 px-0 text-[clamp(22px,3vw,31px)] font-black italic leading-none mt-3 mb-2 min-[900px]:text-[18px] lg:text-[19px] lg:m-0 xl:text-[20px] xl:m-[0_0_8px] desktop-1660:text-[34px] desktop-1660:mb-1 text-shadow-banner"
          >
            AND TRY THE GAMES
          </p>
          <p
            class="relative font-barlow text-white text-left m-0 px-0 text-[clamp(22px,3vw,31px)] font-black italic leading-none min-[900px]:text-[18px] lg:text-[19px] xl:text-[20px] desktop-1660:text-[34px] desktop-1660:mb-8 text-shadow-banner"
          >
            FOR
            <b class="text-[#5bb5fa] font-black italic">FREE</b>
          </p>
        </div>

        <div
          class="relative p-0 w-[227px] mt-10 min-[900px]:w-[192px] min-[900px]:mt-[10px] lg:mt-5 desktop-1660:mt-auto"
        >
          <button
            class="w-full max-w-[400px] rounded-[6px] border border-[#0a9a49] bg-[#0a9a49] text-white text-center font-inter text-base font-bold leading-none tracking-[0.16px] uppercase py-4 px-8 h-[52px] cursor-pointer mx-auto hover:brightness-[103%] active:brightness-[97%] min-[900px]:h-10 min-[900px]:p-0 desktop-1660:py-4 desktop-1660:px-8 desktop-1660:h-[52px]"
            aria-label="Click to start playing"
            role="button"
            @click="openSignUpModal"
          >
            Play Now
          </button>
        </div>
      </div>

      <div class="absolute inset-0 flex items-center">
        <NuxtImg
          format="webp"
          densities="x1 x2"
          class="absolute top-0 right-0 max-h-full h-full"
          :src="DESKTOP_PREVIEW_SRC"
          sizes="(min-width: 720px) 604px"
          :alt="andyAlt"
          loading="eager"
          preload
        />
        <NuxtImg
          format="webp"
          densities="x1 x2"
          role="button"
          class="absolute w-[110px] h-[110px] z-[1] right-[16.5%] mt-6 cursor-pointer min-[900px]:right-[13%] min-[900px]:mt-[26px] desktop-1660:right-[120px]"
          :src="VIDEO_BUTTON_SRC"
          width="110"
          height="110"
          alt="Click to play video"
          aria-label="Click to play video"
          loading="eager"
          preload
          @click="showVideo"
        />
      </div>
    </template>
  </div>
</template>
